import imgPlaceholder from '@images/img-placeholder.svg';

const ERROR_OWNER_SELECTOR = '[data-img-error-own]';

// Swap an image that failed to load for a neutral placeholder, so a missing or
// expired asset reads as empty rather than broken.
export function handleImgErrors() {
    // Keyed on the src attribute as written rather than a marker on the element:
    // a node whose src is reassigned (an Alpine :src binding, the photo viewer
    // stepping through an album) must still get the placeholder when the
    // replacement fails too, which a sticky flag would prevent.
    const applyPlaceholder = (img) => {
        // Some surfaces replace a failed image with context-specific UI.
        if (img.closest(ERROR_OWNER_SELECTOR)) {
            return;
        }
        // The placeholder itself failed. Reassigning it would refetch forever.
        if (img.getAttribute('src') === imgPlaceholder) {
            return;
        }
        img.src = imgPlaceholder;
    };

    // Capture phase: `error` on an element does not bubble, so it is the only
    // phase that reaches window. One delegated listener also covers images
    // inserted after page load, which a per-element attribute cannot.
    window.addEventListener('error', (event) => {
        const img = event.target;
        if (!img || img.tagName !== 'IMG') {
            return;
        }

        applyPlaceholder(img);
    }, true);

    // The bundle is deferred, so images that failed during parse raised their
    // error before this listener existed, and error events do not replay.
    document.querySelectorAll('img').forEach((img) => {
        if (!img.complete || img.naturalWidth !== 0 || !img.getAttribute('src')) {
            return;
        }

        // naturalWidth is 0 for a failed load, but Gecko also reports 0 for a
        // loaded SVG that carries only a viewBox, which the app-download badges
        // do. decode() rejects for the failure and resolves for the SVG, so it
        // separates them without asserting anything about the asset files.
        // Skipped rather than assumed: this runs ahead of the rest of main.js,
        // so a browser without decode() would lose token setup and push
        // registration, not just the sweep.
        if (typeof img.decode !== 'function') {
            return;
        }

        // The recheck discards an AbortError from a src reassigned mid-decode.
        img.decode().catch(() => {
            if (img.complete && img.naturalWidth === 0) {
                applyPlaceholder(img);
            }
        });
    });
}
